import React, { useMemo } from 'react'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import { useDispatch } from 'react-redux'
import { DrawerTab, setTab } from '@/redux/ducks/document/drawer'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import clsx from 'clsx'
import { useAppSelector } from '@/redux/store'
import lang from '@/lang'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  tab: {
    background: theme.backgroundColor,
    borderLeft: theme.border,
    color: '#19233c',
    fontFamily: 'inherit',
    fontWeight: 'normal',
    textTransform: 'none',
    fontSize: 14,
    opacity: 1,
    maxWidth: 'none',
    minWidth: 0,
    flexGrow: 1,
    '&:hover': {
      color: theme.primaryLight,
    },
    '&.first': {
      borderLeft: 0,
    },
  },
  selectedTab: {
    background: theme.white,
    color: theme.primaryLight,
  },
  disabledTab: {
    background: theme.backgroundColor,
    color: '#bbbbbb',
    opacity: '1 !important',
  },
  labelIcon: {
    fontSize: '13px',
  },
  indicator: {
    backgroundColor: theme.primaryLight,
    height: '3px',
    width: '100%',
    transition: 'transform 225ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
  },
  tabsRoot: {
    width: '100%',
    borderBottom: theme.border,
  },
  ocrAvailable: {
    '&$indicator': {
      backgroundColor: theme.colors.yellow,
    },
    '&$selectedTab': {
      color: theme.black,
    },
    '&$tab': {
      backgroundColor: theme.yellowBackground,
    },
  },
  toCheckBadge: {
    display: 'flex',
    alignItems: 'center',
    borderRadius: '16px',
    backgroundColor: theme.colors.yellow,
    padding: '4px 8px',
    marginLeft: '12px',
    color: theme.white,
    fontSize: '12px',
  },
  tabLabel: {
    display: 'flex',
    alignItems: 'center',
  },
}))

type DocumentDrawerTabsProps = {
  tabs: DrawerTab[],
  document: object,
  ocrState: object,
}

type ToCheckBadgeProps = {
  classes: {
    toCheckBadge: string;
  };
}

const ToCheckBadge = React.memo(({ classes }: ToCheckBadgeProps) => {
  return (
    <div className={classes.toCheckBadge}>
      <span>{lang.toCheck}</span>
    </div>
  )
})

type TabLabelProps = {
  label: string;
  index: number;
  ocrState: {
    hasUncheckedOcrValues: boolean;
  };
  tabs: DrawerTab[];
  classes: {
    tabLabel: string;
    toCheckBadge: string;
  };
}

const TabLabel = React.memo(({ label, index, ocrState, tabs, classes }: TabLabelProps) => {
  return (
    <div className={classes.tabLabel}>
      {label}
      {tabs[index].ocrContent && ocrState.hasUncheckedOcrValues && <ToCheckBadge classes={classes} />}
    </div>
  )
})

const DocumentDrawerTabs = (props: DocumentDrawerTabsProps) => {
  const dispatch = useDispatch()
  const activeTab = useAppSelector((state) => state.document.drawer.activeTab)
  const document = useAppSelector((state) => state.document.drawer.document)
  const classes = useStyles({ ...props, document })
  const ocrState = props?.ocrState ?? { hasUncheckedOcrValues: false }

  const {
    tabs,
  } = props

  const handleTabChange = (_e: React.SyntheticEvent, i: number) => {
    dispatch(setTab(i))
  }

  const tabsClasses = useMemo(() => ({
    indicator: clsx(classes.indicator, { [classes.ocrAvailable]: ocrState?.hasUncheckedOcrValues ?? false }),
    root: clsx(classes.tabsRoot),
  }), [classes, activeTab, tabs, ocrState.hasUncheckedOcrValues])

  return (
    <Tabs
      variant="standard"
      orientation="horizontal"
      indicatorColor="secondary"
      textColor="inherit"
      classes={tabsClasses}
      onChange={handleTabChange}
      value={activeTab}>
      {tabs.map((tab, i) => {
        const tabClasses = {
          root: clsx(classes.tab, {
            first: i === 0,
            [classes.ocrAvailable]: tab.ocrContent && ocrState.hasUncheckedOcrValues,
          }),
          labelIcon: classes.labelIcon,
          selected: classes.selectedTab,
          disabled: classes.disabledTab,
        }

        return (
          <Tab
            label={
              <TabLabel
                label={tab.label}
                index={i}
                ocrState={ocrState}
                tabs={tabs}
                classes={classes}
              />
            }
            key={`tab${i}`}
            id={`#documentTab${i}`}
            disabled={tab.disabled}
            classes={tabClasses}
          />
        )
      })}
    </Tabs>
  )
}

export default React.memo(DocumentDrawerTabs)
